/**
 * Paywall for the sponsor console (NOS-170): shown when the org has
 * sponsorships but no entitled subscription (none, or suspended after grace).
 * Sponsored children are never gated — this covers only the console.
 */
import { useState } from "react"
import { CreditCard, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { FOUNDING_PRICE_DOLLARS, SPONSOR_PRICE_DOLLARS } from "@/lib/sponsorBilling"
import type { SponsorSubscription } from "@/hooks/useSponsorSubscription"

export function SponsorSubscriptionGate({
  subscription,
}: {
  subscription: SponsorSubscription
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const suspended = subscription.status === "suspended"

  const handleSubscribe = async () => {
    setBusy(true)
    setError(null)
    const { error: checkoutError } = await subscription.startCheckout()
    if (checkoutError) {
      setError(checkoutError)
      setBusy(false)
    }
    // On success the browser navigates to Stripe Checkout.
  }

  return (
    <div className="mx-auto max-w-lg py-12">
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">
            {suspended ? "Sponsor subscription paused" : "Activate the sponsor suite"}
          </CardTitle>
          <CardDescription>
            {suspended
              ? "Your subscription lapsed — your data is intact and your sponsored projects are unaffected. Resubscribe to restore the console."
              : "One flat subscription for your whole portfolio: the console, fund accounting, transfers, and reporting. Sponsored projects stay on standard usage-based pricing."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-center">
          <div>
            <p className="text-4xl font-bold">
              ${subscription.isFoundingSponsor ? FOUNDING_PRICE_DOLLARS : SPONSOR_PRICE_DOLLARS}
              <span className="text-base font-normal text-muted-foreground">/month</span>
            </p>
            {subscription.isFoundingSponsor ? (
              <p className="mt-1 text-sm text-green-700">
                Founding sponsor rate — locked in for your organization.
              </p>
            ) : (
              <p className="mt-1 text-sm text-muted-foreground">
                ${FOUNDING_PRICE_DOLLARS}/mo founding rate for the first 25 sponsor organizations.
              </p>
            )}
          </div>
          <Button onClick={handleSubscribe} disabled={busy} className="w-full">
            {busy ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <CreditCard className="mr-2 h-4 w-4" aria-hidden />
            )}
            {suspended ? "Resubscribe" : "Subscribe"}
          </Button>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <p className="text-xs text-muted-foreground">
            Billed to your organization via Stripe. Cancel anytime — your sponsored projects keep
            running either way.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
